// 道具面板（Web 简化版） - 财产保险 + 3档金融保险 + 数字保镖 + 自动购买
import { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Statistic,
  Tag,
  App as AntApp,
  Row,
  Col,
  Tooltip,
  Progress,
  Alert,
  Switch,
  Select,
  Slider,
  Divider,
} from 'antd';
import {
  SafetyCertificateOutlined,
  GoldOutlined,
  SecurityScanOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useStore } from '../store/account';
import {
  INSURANCE_PRICE,
  INSURANCE_DURATION_MS,
  FINANCE_INSURANCE_TIERS,
  FINANCE_INSURANCE_DELAY_MS,
  GUARD_PRICE_RATIO,
  GUARD_DURATION_MS,
  GUARD_BLOCK_RATE,
} from '../../shared/constants';
import type { FinanceInsuranceTier } from '../../shared/types';

function formatRemaining(ms: number): string {
  if (ms <= 0) return '已过期';
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}天${hours % 24}小时`;
  if (hours >= 1) return `${hours}小时${mins % 60}分`;
  return `${mins}分`;
}

interface TierCardProps {
  tier: FinanceInsuranceTier;
  onBuy: (tier: FinanceInsuranceTier) => void;
  busy: boolean;
}

function TierCard({ tier, onBuy, busy }: TierCardProps) {
  const { account } = useStore();
  if (!account) return null;

  const conf = FINANCE_INSURANCE_TIERS[tier];
  const total = account.cash + account.marketValue;
  const price = Math.round(total * conf.priceRatio);
  const cashShort = account.cash < price;
  const sameTier = account.financePolicies.filter((p) => p.tier === tier);
  const now = Date.now();
  const activePolicies = sameTier.filter((p) => p.effectiveAt <= now && p.expireAt > now);
  const pendingPolicies = sameTier.filter((p) => p.effectiveAt > now);
  const capDisplay = conf.payoutMultiplier === Infinity ? '∞ 无上限' : `× ${conf.payoutMultiplier}`;

  const tierColors: Record<FinanceInsuranceTier, string> = {
    tier1: '#52c41a',
    tier2: '#1677ff',
    tier3: '#ffd700',
  };
  const tierNames: Record<FinanceInsuranceTier, string> = {
    tier1: '基础档',
    tier2: '标准档',
    tier3: '尊享档',
  };

  return (
    <Card
      size="small"
      style={{
        background: '#1c2128',
        borderColor: tierColors[tier],
        borderWidth: 1,
      }}
      title={
        <Space>
          <GoldOutlined style={{ color: tierColors[tier] }} />
          <span style={{ color: tierColors[tier] }}>{tierNames[tier]}</span>
          <Tag color={tier === 'tier3' ? 'gold' : tier === 'tier2' ? 'blue' : 'green'}>
            保费 {conf.priceRatio * 100}%
          </Tag>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Statistic
          title="本次价格"
          value={price}
          prefix="¥"
          valueStyle={{ fontSize: 20, color: tierColors[tier] }}
        />
        <div style={{ fontSize: 11, color: '#8b949e' }}>
          <Tag color="purple">赔付 {capDisplay}</Tag>
          <Tag>有效期 {conf.durationMs / 86400000} 天</Tag>
        </div>

        {pendingPolicies.length > 0 && (
          <Alert
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            message={`${pendingPolicies.length} 份等待生效`}
          />
        )}

        {activePolicies.length > 0 && (
          <Tag color="processing">✅ {activePolicies.length} 份生效中</Tag>
        )}

        <Button
          type="primary"
          block
          onClick={() => onBuy(tier)}
          loading={busy}
          disabled={cashShort}
          style={{ background: tierColors[tier], borderColor: tierColors[tier] }}
        >
          {cashShort
            ? `现金不足 (需 ¥${price.toLocaleString()})`
            : `购买 ¥${price.toLocaleString()}`}
        </Button>
      </Space>
    </Card>
  );
}

export default function ItemsPanel() {
  const { account, refreshAccount, buyInsurance, buyFinanceInsurance, buyGuard, setAutoInsurance } =
    useStore();
  const { message, modal } = AntApp.useApp();
  const [busy, setBusy] = useState<'insurance' | FinanceInsuranceTier | 'guard' | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!account) return null;

  const totalValue = account.cash + account.marketValue;
  const guardPrice = Math.round(totalValue * GUARD_PRICE_RATIO);
  const insuranceRemaining = account.items.insuranceUntil - Date.now();

  const handleBuyInsurance = async () => {
    setBusy('insurance');
    try {
      const r = await buyInsurance();
      if (r.ok) message.success(r.message);
      else message.warning(r.message);
      await refreshAccount();
    } finally {
      setBusy(null);
    }
  };

  const handleBuyFinance = (tier: FinanceInsuranceTier) => {
    const conf = FINANCE_INSURANCE_TIERS[tier];
    const total = account.cash + account.marketValue;
    const price = Math.round(total * conf.priceRatio);
    const sameTier = account.financePolicies.filter((p) => p.tier === tier);
    const hasActive = sameTier.some((p) => p.effectiveAt <= Date.now() && p.expireAt > Date.now());

    modal.confirm({
      title: `购买 ${conf.label}？`,
      width: 480,
      content: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Alert
            type={hasActive ? 'success' : 'warning'}
            showIcon
            message={
              hasActive
                ? '续期模式：本保单将无缝续期（无 2 小时延迟）'
                : `⚠️ 首次购买需等待 ${FINANCE_INSURANCE_DELAY_MS / 3600000} 小时生效`
            }
          />
          <div>
            本次价格：<b style={{ color: '#faad14' }}>¥{price.toLocaleString()}</b>
          </div>
        </Space>
      ),
      okText: `确认支付 ¥${price.toLocaleString()}`,
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(tier);
        try {
          const r = await buyFinanceInsurance(tier);
          if (r.ok) message.success(r.message);
          else message.warning(r.message);
          await refreshAccount();
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const handleBuyGuard = async () => {
    setBusy('guard');
    try {
      const r = await buyGuard();
      if (r.ok) message.success(r.message);
      else message.warning(r.message);
      await refreshAccount();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {/* 财产保险 */}
      <Card
        size="small"
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: '#faad14' }} />
            <span>财产保险</span>
            <Tag>即买即生效</Tag>
          </Space>
        }
        style={{ background: '#1c2128', borderColor: '#30363d' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Statistic
            title="价格"
            value={INSURANCE_PRICE}
            prefix="¥"
            valueStyle={{ fontSize: 18, color: '#faad14' }}
          />
          <div style={{ fontSize: 12, color: '#8b949e' }}>
            激活后 {INSURANCE_DURATION_MS / 60000} 分钟内首次被偷{' '}
            <Tag color="success">100% 返还</Tag>
          </div>
          {insuranceRemaining > 0 ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Tag color="processing">已激活</Tag>
              <Progress
                percent={Math.max(
                  0,
                  Math.min(100, (insuranceRemaining / INSURANCE_DURATION_MS) * 100)
                )}
                showInfo={false}
                size="small"
                strokeColor="#faad14"
              />
              <span style={{ fontSize: 11, color: '#8b949e' }}>
                剩余 {formatRemaining(insuranceRemaining)}
                {account.items.insuranceUsed && ' · 已用 1 次'}
              </span>
            </Space>
          ) : (
            <Tag>未激活</Tag>
          )}
          <Button
            type="primary"
            danger={insuranceRemaining <= 0}
            block
            onClick={handleBuyInsurance}
            loading={busy === 'insurance'}
            disabled={insuranceRemaining > 0 && !account.items.insuranceUsed}
          >
            {insuranceRemaining > 0 && !account.items.insuranceUsed
              ? '已激活'
              : `购买 ¥${INSURANCE_PRICE.toLocaleString()}`}
          </Button>
        </Space>
      </Card>

      {/* 3 档金融保险 */}
      <div>
        <Space style={{ marginBottom: 8 }}>
          <GoldOutlined style={{ color: '#ffd700' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>金融保险（3 档可选 · 可叠加）</span>
          <Tooltip title="金融保险赔付能力强，但需等 2 小时生效（续期除外）。只允许叠加同档保单。">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
        <Row gutter={8}>
          <Col span={8}>
            <TierCard tier="tier1" onBuy={handleBuyFinance} busy={busy === 'tier1'} />
          </Col>
          <Col span={8}>
            <TierCard tier="tier2" onBuy={handleBuyFinance} busy={busy === 'tier2'} />
          </Col>
          <Col span={8}>
            <TierCard tier="tier3" onBuy={handleBuyFinance} busy={busy === 'tier3'} />
          </Col>
        </Row>
      </div>

      {/* 自动购买 */}
      <Card
        size="small"
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#722ed1' }} />
            <span>自动购买保险</span>
          </Space>
        }
        style={{ background: '#1c2128', borderColor: '#30363d' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Space>
            <Switch
              checked={account.autoInsurance.enabled}
              onChange={async (checked) => {
                await setAutoInsurance({ enabled: checked });
                await refreshAccount();
                message.success(checked ? '已开启自动保险' : '已关闭');
              }}
            />
            <span style={{ fontSize: 12 }}>
              {account.autoInsurance.enabled ? '已开启' : '关闭'}
            </span>
          </Space>
          <div style={{ fontSize: 11, color: '#8b949e' }}>
            开启后，当现金低于总资产阈值时自动购买指定档保险
          </div>
          {account.autoInsurance.enabled && (
            <>
              <div style={{ fontSize: 11 }}>
                现金比例阈值：{(account.autoInsurance.cashRatio * 100).toFixed(0)}%
              </div>
              <Slider
                min={5}
                max={50}
                step={5}
                value={account.autoInsurance.cashRatio * 100}
                onChangeComplete={async (v) => {
                  await setAutoInsurance({ enabled: true, cashRatio: v / 100 });
                  await refreshAccount();
                }}
              />
              <Select
                size="small"
                style={{ width: '100%' }}
                value={account.autoInsurance.tier}
                onChange={async (tier) => {
                  await setAutoInsurance({ enabled: true, tier });
                  await refreshAccount();
                }}
                options={[
                  { value: 'tier1', label: '基础档 (5%·便宜·赔付低)' },
                  { value: 'tier2', label: '标准档 (10%·平衡)' },
                  { value: 'tier3', label: '尊享档 (20%·贵·无上限)' },
                ]}
              />
            </>
          )}
        </Space>
      </Card>

      <Divider style={{ margin: '4px 0' }} />

      {/* 数字保镖 */}
      <Card
        size="small"
        title={
          <Space>
            <SecurityScanOutlined style={{ color: '#1677ff' }} />
            <span>数字保镖</span>
            <Tag color="cyan">20% 拦截</Tag>
          </Space>
        }
        style={{ background: '#1c2128', borderColor: '#30363d' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Statistic
            title={`价格 (总市值 ${GUARD_PRICE_RATIO * 100}%)`}
            value={guardPrice}
            prefix="¥"
            valueStyle={{ fontSize: 18, color: '#1677ff' }}
          />
          <div style={{ fontSize: 12, color: '#8b949e' }}>
            雇佣后 {GUARD_DURATION_MS / 3600000} 小时，每次事件{' '}
            <Tag color="processing">{GUARD_BLOCK_RATE * 100}% 概率</Tag>拦截
          </div>
          {account.items.guardUntil > Date.now() ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Tag color="processing">已雇佣 · 剩余 {account.items.guardCharges} 次</Tag>
              <Progress
                percent={Math.max(
                  0,
                  Math.min(100, ((account.items.guardUntil - Date.now()) / GUARD_DURATION_MS) * 100)
                )}
                showInfo={false}
                size="small"
                strokeColor="#1677ff"
              />
              <span style={{ fontSize: 11, color: '#8b949e' }}>
                剩余 {formatRemaining(account.items.guardUntil - Date.now())}
              </span>
            </Space>
          ) : (
            <Tag>未雇佣</Tag>
          )}
          <Button
            type="primary"
            block
            onClick={handleBuyGuard}
            loading={busy === 'guard'}
            disabled={account.cash < guardPrice}
          >
            {account.items.guardUntil > Date.now()
              ? `续费 ¥${guardPrice.toLocaleString()}`
              : `雇佣 ¥${guardPrice.toLocaleString()}`}
          </Button>
        </Space>
      </Card>
    </Space>
  );
}
